import { SliceComponentProps } from "@prismicio/react";
import intervalToDuration from "date-fns/intervalToDuration";
import isExists from "date-fns/isExists";
import _get from "lodash/get";
import { useRouter } from "next/router";
import { RegisterOptions, useFormContext } from "react-hook-form";

import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import DateInput from "components/form/dateInput/DateInput";
import RadioGroup from "components/form/radioGroup/RadioGroup";
import { DateInputFieldSlice } from "prismicio-types";
import { useGetProduct } from "queries/product-queries";
import { DropdownSelectSchemaField, FieldSliceContext } from "types/schema";
import {
  prismicFieldConfigurationError,
  SSR_CONFIGURATION_ERROR,
} from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";
import {
  getInputPropsFromSchemaRules,
  isFieldType,
  useFindByKey,
} from "utils/schema-utils";

import styles from "../Fields.module.scss";

export default function DateInputField({
  slice,
}: SliceComponentProps<DateInputFieldSlice, FieldSliceContext>) {
  const {
    formState: { errors },
    watch,
  } = useFormContext();

  const { data: product } = useGetProduct();

  const findByKey = useFindByKey();
  const router = useRouter();
  const interpolate = useInterpolate();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const schemaKey = slice.primary.muse_schema_key;
  const yearsCountSchemaKey = slice.primary.years_count_muse_schema_key;

  if (!schemaKey) {
    throw new Error("Missing Muse Schema Key");
  }

  const [yearKey, monthKey, dayKey] = schemaKey.split(",") as string[];

  const yearField = findByKey(yearKey);
  const monthField = findByKey(monthKey);
  const dayField = findByKey(dayKey);

  if (!yearField || !monthField || !dayField) {
    return null;
  }

  const [year, month, day] = watch([yearKey, monthKey, dayKey]);

  if (
    !isFieldType<DropdownSelectSchemaField>(yearField, "dropdown_select") ||
    !isFieldType<DropdownSelectSchemaField>(monthField, "dropdown_select") ||
    !isFieldType<DropdownSelectSchemaField>(dayField, "dropdown_select")
  ) {
    throw prismicFieldConfigurationError(
      schemaKey,
      "dropdown_select",
      "multiple"
    );
  }

  function getYears() {
    if (
      !year ||
      !month ||
      !day ||
      !isExists(Number(year), Number(month - 1), Number(day))
    ) {
      return null;
    }

    const start = new Date(year, month - 1, day);
    return intervalToDuration({ start, end: new Date() }).years || null;
  }

  const { maxLength: maxYear, minLength: minYear } =
    getInputPropsFromSchemaRules(yearField.rules);

  if (!maxYear || !minYear) {
    throw new Error("Missing max or min year in product");
  }

  const registerOptions: RegisterOptions = {
    required: interpolate(
      product.content.canvas.form_field_required_error_message ||
        CANVAS_FALLBACK_CONTENT.form_field_required_error_message
    ),
  };

  const currentYears = getYears();

  const dateError =
    errors &&
    (
      _get(errors, yearField.urlKey) ||
      _get(errors, monthField.urlKey) ||
      _get(errors, dayField.urlKey)
    )?.message;

  return (
    <div className={styles.field}>
      {slice.primary.label && (
        <label
          className={styles.label}
          htmlFor={
            router.query.locale === "en-US"
              ? monthField.urlKey
              : dayField.urlKey
          }
          // ^ TODO: more robust approach to targetting first date field
        >
          {interpolate(slice.primary.label)}
        </label>
      )}

      <ErrorBoundary>
        <DateInput
          registerOptions={registerOptions}
          day={{
            name: dayField.urlKey,
            label: slice.primary.day_label!,
          }}
          month={{
            name: monthField.urlKey,
            label: slice.primary.month_label!,
          }}
          year={{
            name: yearField.urlKey,
            min: minYear,
            max: maxYear,
            label: slice.primary.year_label!,
          }}
        />
      </ErrorBoundary>

      {dateError && <p className={styles.error}>{dateError as string}</p>}

      {yearsCountSchemaKey &&
        (() => {
          const yearsCountField = findByKey(yearsCountSchemaKey);

          if (!yearsCountField) {
            return null;
          }

          const yearsError =
            errors && _get(errors, yearsCountSchemaKey)?.message;

          return (
            <div style={{ marginTop: "0.5rem" }}>
              {slice.primary.years_count_label && (
                <label
                  className={styles.label}
                  htmlFor={yearsCountField.urlKey}
                >
                  {interpolate(slice.primary.years_count_label)}
                </label>
              )}

              <ErrorBoundary>
                <RadioGroup
                  name={yearsCountField.urlKey}
                  registerOptions={{ required: registerOptions.required }}
                  options={{
                    current: `${slice.primary.years_count_option_current} ${
                      currentYears !== null ? `(${currentYears})` : ``
                    }`,
                    next: `${slice.primary.years_count_option_next} ${
                      currentYears !== null ? `(${currentYears + 1})` : ``
                    }`,
                  }}
                />
              </ErrorBoundary>

              {yearsError && (
                <p className={styles.error}>{yearsError as string}</p>
              )}
            </div>
          );
        })()}
    </div>
  );
}
